import { Box, Flex, Text, Icon, IconButton } from '@chakra-ui/react';
import { forwardRef } from 'react';
import { CloseIcon } from '@chakra-ui/icons';
import { TbSpeakerphone } from 'react-icons/tb';
import { AnnouncementItem } from '../../hooks/useGetNotifications';

interface AnnouncementBannerProps {
    announcement: AnnouncementItem;
    /** Number of additional unread announcements beyond the one shown. */
    extraCount?: number;
    onDismiss: () => void;
    isDismissing?: boolean;
}

/**
 * Window during which the banner wears a festive red/white/blue theme for the
 * Fourth of July. Reverts to the standard partbay blue styling automatically
 * once this date passes, so no follow-up cleanup is needed.
 */
const AMERICAN_THEME_UNTIL = new Date('2026-07-07T00:00:00-04:00');

const isAmericanThemeActive = () => new Date() < AMERICAN_THEME_UNTIL;

const NAVY = '#1B2A5E';
const RED = '#B22234';

/**
 * Decorative flowing flag ribbons inspired by the Fourth of July artwork.
 * Rendered as a non-interactive SVG anchored to one edge and faded toward the
 * center so it never sits behind the announcement text (keeps text readable).
 */
const FlagRibbon = ({ flip = false }: { flip?: boolean }) => (
    <Box
        position="absolute"
        top={0}
        bottom={0}
        {...(flip ? { left: 0 } : { right: 0 })}
        w={{ base: '90px', md: '150px' }}
        pointerEvents="none"
        overflow="hidden"
        aria-hidden
        sx={{
            transform: flip ? 'scaleX(-1)' : undefined,
            maskImage: 'linear-gradient(to left, black 55%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to left, black 55%, transparent 100%)',
        }}
    >
        <svg
            width="100%"
            height="100%"
            viewBox="0 0 150 44"
            preserveAspectRatio="xMaxYMid slice"
            style={{ display: 'block' }}
        >
            <path d="M20 -5 C70 8, 90 20, 160 12 L160 -5 Z" fill={RED} opacity="0.85" />
            <path d="M35 52 C85 30, 105 42, 160 30 L160 52 Z" fill={NAVY} opacity="0.85" />
            <path d="M55 22 C95 12, 120 26, 160 18 L160 30 C120 34, 95 20, 55 30 Z" fill={RED} opacity="0.55" />
            <path d="M70 6 C110 2, 130 14, 160 8 L160 16 C130 20, 110 10, 70 14 Z" fill={NAVY} opacity="0.45" />
        </svg>
    </Box>
);

/**
 * Full-width announcement bar rendered directly under the top bar. Styled in
 * partbay blue by default (mirrors the disclaimer banner pattern), but wears a
 * temporary red/white/blue Fourth of July theme through {@link AMERICAN_THEME_UNTIL}.
 * Pushes page content down via the measured height in the app layout.
 */
const AnnouncementBanner = forwardRef<HTMLDivElement, AnnouncementBannerProps>(
    ({ announcement, extraCount = 0, onDismiss, isDismissing = false }, ref) => {
        const american = isAmericanThemeActive();

        const bg = american ? '#FFFFFF' : '#E6F1FD';
        const borderColor = american ? RED : '#0071E3';
        const iconColor = american ? RED : '#0071E3';
        const textColor = american ? NAVY : 'gray.800';
        const senderColor = american ? 'rgba(27,42,94,0.6)' : 'gray.500';
        const closeColor = american ? NAVY : '#0071E3';
        const closeHoverBg = american ? '#f0d5d9' : '#d5d9f0';

        return (
            <Box
                ref={ref}
                bg={bg}
                color={textColor}
                borderTop={american ? `3px solid ${NAVY}` : undefined}
                borderBottom="3px solid"
                borderColor={borderColor}
                position="relative"
                py={2}
                px={{ base: '40px', md: '120px' }}
                minH="44px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                overflow="hidden"
            >
                {american && (
                    <>
                        <FlagRibbon flip />
                        <FlagRibbon />
                    </>
                )}

                <Flex align="flex-start" justify="center" gap={2} zIndex={1}>
                    <Icon
                        as={TbSpeakerphone as React.ElementType}
                        w={4}
                        h={4}
                        mt="2px"
                        flexShrink={0}
                        color={iconColor}
                    />
                    <Text
                        fontSize="sm"
                        fontWeight={american ? 'medium' : 'normal'}
                        color={textColor}
                        textAlign="center"
                        lineHeight="short"
                        whiteSpace="pre-wrap"
                    >
                        "{announcement.message}"
                        <Text as="span" fontSize="xs" color={senderColor} ml={1}>
                            - {announcement.senderName}{extraCount > 0 ? ` (+${extraCount})` : ''}
                        </Text>
                    </Text>
                </Flex>

                <IconButton
                    aria-label="Dismiss announcement"
                    icon={<CloseIcon boxSize={2.5} />}
                    size="xs"
                    variant="ghost"
                    color={closeColor}
                    position="absolute"
                    right={2}
                    top="50%"
                    transform="translateY(-50%)"
                    zIndex={2}
                    _hover={{ bg: closeHoverBg }}
                    onClick={onDismiss}
                    isLoading={isDismissing}
                />
            </Box>
        );
    }
);

AnnouncementBanner.displayName = 'AnnouncementBanner';

export default AnnouncementBanner;
